import React from 'react'

import { Tooltip, TooltipItem, TooltipPositionerFunction } from 'chart.js'
import ChartJSDatalabels from 'chartjs-plugin-datalabels'
import { Bar as CharJSHorizontalBar, ChartProps } from 'react-chartjs-2'

import { useIntl } from '@context/Internationalization/useIntl'
import { defaultTheme, IThemeInterface } from '../../Theme'

declare module 'chart.js' {
  // Augmenting chart.js's own TooltipPositionerMap interface; the name must
  // match chart.js's declaration verbatim for TS declaration merging to apply.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface TooltipPositionerMap {
    cursor: TooltipPositionerFunction<'bar'>
  }
}

// Chart.js's built-in tooltip positioners ('average' | 'nearest') both
// resolve to the ACTIVE ELEMENT's own tooltipPosition(), never the cursor.
// For a horizontal BarElement that's {x: <bar's END/tip pixel>, y: <row
// center>} — BarController#updateElements sets `x: horizontal ? vpixels.head
// : ipixels.center` — so with one bar per row the tooltip caret pins to the
// bar's tip and floats away from wherever the pointer actually is along the
// row (BRAND-9483 e2e finding). Switching to 'nearest' would NOT have fixed
// this: with a single dataset there's only ever one active element, so
// 'nearest' calls that same element's tooltipPosition() and returns an
// identical result to 'average'. Registering a positioner keyed off the raw
// event coordinates instead makes the tooltip track the cursor directly.
//
// yAlign: 'top' forces the tooltip BODY below the point (caret on the
// tooltip's top edge) so it never covers the bar being read. Chart.js
// honours alignment returned by a positioner: Tooltip#update merges the
// positioner's result into positionAndSize, and determineAlignment prefers
// `size.yAlign` over the options/auto-derived values. Near the chart bottom
// (e.g. the last "Children & Baby" row) there's no room below the cursor,
// so the positioner flips to yAlign 'bottom' (body ABOVE the cursor) when
// the remaining space can't fit the tooltip.

// Flip threshold for the very first hover, before the tooltip has ever been
// sized (~tooltip height + caret + margin).
const TOOLTIP_FLIP_FALLBACK_THRESHOLD = 90
// Caret + breathing room added on top of a known tooltip height.
const TOOLTIP_FLIP_MARGIN = 16

Tooltip.positioners.cursor = function (_activeElements, eventPosition) {
  if (!eventPosition) {
    return false
  }
  // Positioners run with `this` = the Tooltip instance (Chart.js invokes
  // `positioners[options.position].call(this, ...)`), which exposes `.chart`
  // and the PRIOR render's `.height` — the positioner runs before the
  // current render's size is computed, so height is undefined on the very
  // first hover (hence the fallback threshold).
  const threshold = this.height
    ? this.height + TOOLTIP_FLIP_MARGIN
    : TOOLTIP_FLIP_FALLBACK_THRESHOLD
  const roomBelow = this.chart.chartArea.bottom - eventPosition.y
  return {
    x: eventPosition.x,
    y: eventPosition.y,
    yAlign: roomBelow < threshold ? 'bottom' : 'top',
  }
}

interface ICharData {
  label: string
  value: number
  percentage: number
}

interface IHorizontalBarProps {
  data: ReadonlyArray<ICharData>
  showAsPercentage?: boolean
  roundPercentage?: boolean
  theme: IThemeInterface
  showEmpty?: boolean
}

export const defaultProps = {
  showAsPercentage: false,
  theme: defaultTheme,
}

// Canvas-rendered category tick labels are right-anchored and draw past the
// chart's edge once they exceed the width Chart.js actually allocates to the
// y-axis in the 2-col ChartGrid. A flat character-count cutoff (previously a
// fixed 24 chars) assumes an average glyph width that doesn't hold across
// card widths or fonts, so it still clipped at real card widths (e.g.
// "Clothes, Shoes, Handbags & Accessories" rendering as "hoes, Handbag…" —
// BRAND-9483 e2e follow-up). Chart.js has no built-in truncation for this
// axis, so we ellipsize long labels ourselves, measuring against the
// scale's actual per-render pixel budget (see the ticks.callback below,
// which reads `this.maxWidth` — Chart.js's Scale#update sets that before
// generating tick labels, ahead of Scale#fit clamping the final width to
// it) instead of a fixed character count.
export const Y_AXIS_LABEL_ELLIPSIS = '…'

/**
 * Trims `label` (appending an ellipsis) until `measureText` reports it fits
 * within `maxWidth`. Pure and canvas-agnostic on purpose: production passes
 * a `ctx.measureText`-backed function (matched to the tick font); tests pass
 * a cheap mock so this stays unit-testable without a real canvas.
 */
export const truncateYAxisLabel = (
  label: string,
  maxWidth: number,
  measureText: (text: string) => number,
): string => {
  if (!Number.isFinite(maxWidth) || maxWidth <= 0 || measureText(label) <= maxWidth) {
    return label
  }

  // Binary-search the longest prefix that fits alongside the ellipsis.
  let low = 0
  let high = label.length
  let best = Y_AXIS_LABEL_ELLIPSIS

  while (low <= high) {
    const mid = Math.floor((low + high) / 2)
    const candidate = `${label.slice(0, mid).trimEnd()}${Y_AXIS_LABEL_ELLIPSIS}`

    if (measureText(candidate) <= maxWidth) {
      best = candidate
      low = mid + 1
    } else {
      high = mid - 1
    }
  }

  return best
}

// Reserves a small buffer below the scale's granted width so measured text
// never draws flush against Chart.js's own tick padding.
const Y_AXIS_LABEL_WIDTH_BUFFER = 8
// Single font family shared by every text surface this chart draws: y-axis
// ticks, bar-end datalabels, the empty-state subtitle, and the tooltip.
const CHART_FONT_FAMILY = 'Rubik'
const Y_AXIS_TICK_FONT_SIZE = 14
// CSS font shorthand equivalent of the ticks.font config below — applied to
// the canvas context before measuring so measureText sees the tick font.
const Y_AXIS_TICK_FONT = `${Y_AXIS_TICK_FONT_SIZE}px ${CHART_FONT_FAMILY}`

// Shared by the bar-end datalabels and the tooltip value line so both surfaces
// format numbers identically (2dp for fractional values, thousands-separated
// integers otherwise, optional trailing '%').
export const formatChartValue = (value: number | string, showAsPercentage: boolean): string => {
  const num = typeof value === 'number' ? value : parseFloat(value)
  const formatted =
    num % 1 === 0
      ? num.toLocaleString()
      : num.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
  return `${formatted}${showAsPercentage ? '%' : ''}`
}

// The y-axis ellipsizes long labels on-canvas (truncateYAxisLabel, above) so
// they stop clipping past the chart edge, but that leaves no way to read the
// full text (BRAND-9483 follow-up: the ticket's expected behaviour pairs the
// ellipsis with a hover reveal). This mirrors the dark hover-card already used
// for full-label reveal elsewhere (AudienceShareBarChart / PackedBubbleChart's
// D3 tooltips, BRAND-9253): grey900Color background, white Rubik text, 4px
// corner radius, 8/12px padding, no colour swatch. Ported to Chart.js's native
// tooltip plugin (rather than the DOM-ref + mousemove approach those D3 charts
// use) since HorizontalBar renders via chart.js canvas, not an SVG we control.
export const getHorizontalBarTooltipOptions = (
  theme: IThemeInterface,
  data: ReadonlyArray<ICharData>,
  dataPoints: ReadonlyArray<number>,
  showAsPercentage: boolean,
) => ({
  enabled: true,
  // Follows the cursor (see the `Tooltip.positioners.cursor` registration
  // above) instead of Chart.js's default element-anchored positioners,
  // which pinned the caret to the bar's far end (BRAND-9483 e2e finding).
  position: 'cursor' as const,
  backgroundColor: theme.grey900Color,
  titleColor: theme.whiteColor,
  bodyColor: theme.whiteColor,
  titleFont: {
    family: CHART_FONT_FAMILY,
    size: 14,
    weight: '600',
  },
  bodyFont: {
    family: CHART_FONT_FAMILY,
    size: 12,
  },
  padding: { top: 8, right: 12, bottom: 8, left: 12 },
  cornerRadius: 4,
  displayColors: false,
  callbacks: {
    // Full, untruncated label — the raw data label, not the ellipsized tick
    // text the y-scale renders on-canvas.
    title: (items: TooltipItem<'bar'>[]) => data[items[0]?.dataIndex ?? -1]?.label ?? '',
    label: (item: TooltipItem<'bar'>) =>
      formatChartValue(dataPoints[item.dataIndex], showAsPercentage),
  },
})

export const HorizontalBar: React.FC<IHorizontalBarProps> = ({
  data,
  showAsPercentage = false,
  roundPercentage,
  theme = defaultTheme,
  showEmpty,
}) => {
  const { intl } = useIntl()
  const dataPoints = data.map((dat) => {
    if (showAsPercentage) {
      return roundPercentage ? Math.round(dat.percentage) : dat.percentage
    }
    return dat.value
  })
  const labels = data.map((dat) => dat.label)

  // Find all indices with the highest value (decimal-level comparison)
  const highestValue = dataPoints.length > 0 ? Math.max(...dataPoints) : 0
  const highestValueIndices = dataPoints
    .map((v, i) => (v === highestValue ? i : -1))
    .filter((i) => i !== -1)
  const backgroundColor = dataPoints.map((value) =>
    value === highestValue ? theme.primaryColor : theme.secondary500Color,
  )

  const horizontalBarOptions: ChartProps<'bar'>['options'] = {
    indexAxis: 'y',
    layout: {
      padding: {
        top: 0,
        right: 100,
        bottom: 0,
        left: 0,
      },
    },
    scales: {
      x: {
        border: {
          display: false,
        },
        grid: {
          display: false,
        },
        ticks: {
          display: false,
        },
      },
      y: {
        border: {
          display: false,
        },
        grid: {
          display: false,
        },
        ticks: {
          font: {
            family: CHART_FONT_FAMILY,
            size: Y_AXIS_TICK_FONT_SIZE,
          },
          color: theme.grey700Color,
          callback: function (value) {
            const label = this.getLabelForValue(value as number)
            // `this.maxWidth` is the pixel budget Chart.js's Scale#update
            // grants THIS axis for the current layout pass, set before tick
            // labels are generated (and before Scale#fit clamps the scale's
            // final width to it) — i.e. the real per-render allocation the
            // 2-col ChartGrid gives this card's y-axis, not a guess.
            this.ctx.font = Y_AXIS_TICK_FONT
            return truncateYAxisLabel(
              label,
              this.maxWidth - Y_AXIS_LABEL_WIDTH_BUFFER,
              (text) => this.ctx.measureText(text).width,
            )
          },
        },
      },
    },
    plugins: {
      tooltip: getHorizontalBarTooltipOptions(theme, data, dataPoints, showAsPercentage),
      legend: {
        display: false,
      },
    },
  }

  if (showEmpty) {
    return (
      <CharJSHorizontalBar
        data={{
          labels: ['', '', '', '', ''], // number of skeleton bars
          datasets: [
            {
              data: [100, 60, 70, 50, 10], // arbitrary values for bar lengths
              backgroundColor: theme.grey300Color,
              barThickness: 24,
            },
          ],
        }}
        plugins={[ChartJSDatalabels]}
        options={{
          ...horizontalBarOptions,
          // No datalabels in the empty state, so drop the right padding the data
          // state reserves for them — this lets the caption centre in the card.
          layout: { padding: { top: 0, right: 0, bottom: 0, left: 0 } },
          scales: {
            ...horizontalBarOptions.scales,
            x: {
              ...horizontalBarOptions.scales?.x,
              max: 100,
            },
            y: {
              ...horizontalBarOptions.scales?.y,
              ticks: {
                display: false,
              },
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            subtitle: {
              display: true,
              position: 'bottom',
              align: 'center',
              padding: { top: 20 },
              text: intl.formatMessage({ id: 'audienceInsights.chart.empty' }),
              color: theme.grey700Color,
              font: {
                family: CHART_FONT_FAMILY,
                size: 14,
              },
            },
            tooltip: {
              enabled: false,
            },
            datalabels: {
              display: false,
            },
          },
        }}
      />
    )
  }

  const config: ChartProps<'bar'>['data'] = {
    labels,
    datasets: [
      {
        data: dataPoints,
        backgroundColor,
        datalabels: {
          // Anchor at the bar's END (right tip) and align past it so the % label
          // renders in the reserved right gutter (layout.padding.right) OUTSIDE
          // the bar. anchor:'start' (introduced in the chart.js v4 upgrade,
          // BRAND-7688) drew the label inside the bar fill, where the
          // bar-coloured label text had zero contrast against the matching bar
          // colour and disappeared (BRAND-9463).
          anchor: 'end',
          offset: 2,
          align: 'end',
          display: true,
          color: (curr) => {
            // Highlight all highest value labels
            return highestValueIndices.includes(curr.dataIndex)
              ? theme.primaryColor
              : theme.secondary500Color
          },
          font: {
            size: 14,
            family: CHART_FONT_FAMILY,
          },
          // Enable decimals in formatter (show up to 2 decimals if needed)
          formatter: (value) => formatChartValue(value, showAsPercentage),
        },
      },
    ],
  }

  // Custom y-axis tick color for highest value labels
  const optionsWithLabelHighlight = {
    ...horizontalBarOptions,
    scales: {
      ...horizontalBarOptions.scales,
      y: {
        ...horizontalBarOptions.scales?.y,
        ticks: {
          ...horizontalBarOptions.scales?.y?.ticks,
          color: (ctx) => {
            // ctx.index is the tick index
            return highestValueIndices.includes(ctx.index) ? theme.primaryColor : theme.grey700Color
          },
        },
      },
    },
  }
  return (
    <CharJSHorizontalBar
      data={config}
      plugins={[ChartJSDatalabels]}
      options={optionsWithLabelHighlight}
    />
  )
}

export type { IHorizontalBarProps }
